import { useCallback, ReactElement, ReactNode, FC } from 'react';
import { SelectField, SelectFieldProps } from '@ff-it/form';
import { useOptions, defaultRequestHandler } from '../useOptions';
import { Page } from '../useOptions/types';
import { api } from 'services';
import useSWR from 'swr';

export type BoundProps = Omit<SelectFieldProps<any, boolean>, 'options' | 'loadOptions'> & {
  filter?: Record<string, unknown>;
};

export function useResolveSimple<T>(
  url: string,
  filter?: Record<string, unknown>,
): (values: (string | number)[], signal?: AbortSignal) => Promise<readonly T[]> {
  return useCallback(
    async (values, signal) => {
      // Bridge the resolver's AbortSignal to api's abortController so in-flight
      // resolves are cancelled when the value changes or the field unmounts.
      const controller = new AbortController();
      const onAbort = () => controller.abort();
      signal?.addEventListener('abort', onAbort, { once: true });
      try {
        const { queryParams } = defaultRequestHandler(url, undefined, {
          id__in: values.join(','),
          ...filter,
        });
        const res = await api.request<Page<T>, unknown>({
          url,
          method: 'GET',
          queryParams,
          abortController: controller,
        });
        // jo swallows rejections; throwing here keeps the UI responsive instead
        // of surfacing an uncaught promise rejection.
        if (!res.ok) throw res.error;
        return res.data.results;
      } finally {
        signal?.removeEventListener('abort', onAbort);
      }
    },
    [url, filter],
  );
}

function noOptionsMessage({ inputValue }: { inputValue: string }): ReactNode {
  return inputValue ? 'No matches' : 'Type to search';
}

function AsyncSelectFieldBase({
  url,
  filter,
  suggestion,
  ...props
}: BoundProps & { url: string; suggestion?: boolean }): ReactElement {
  const loadOptions = useOptions<any>(url, filter, { suggestion });
  return <SelectField loadOptions={loadOptions} noOptionsMessage={noOptionsMessage} {...props} />;
}

function SimpleAsyncSelect<T>({
  url,
  filter,
  suggestion,
  ...props
}: BoundProps & { url: string; suggestion?: boolean }): ReactElement {
  const loadOptions = useOptions<any>(url, filter, { suggestion });
  const resolveSimple = useResolveSimple<T>(url, filter);
  return (
    <SelectField
      loadOptions={loadOptions}
      resolveSimple={resolveSimple}
      noOptionsMessage={noOptionsMessage}
      {...props}
    />
  );
}

export function createSelectField<T>(
  optionsOrUrl: string | readonly T[],
  extra?: Partial<SelectFieldProps<any, boolean>> & { filter?: Record<string, unknown>; suggestion?: boolean },
): FC<BoundProps> {
  function BoundSelect(props: BoundProps): ReactElement {
    if (typeof optionsOrUrl === 'string') {
      return props.simple ? (
        <SimpleAsyncSelect<T> url={optionsOrUrl} {...extra} {...props} />
      ) : (
        <AsyncSelectFieldBase url={optionsOrUrl} {...extra} {...props} />
      );
    }
    return <SelectField options={optionsOrUrl} {...extra} {...props} />;
  }

  return BoundSelect;
}

export function createStaticField<T>(url: string, extra: Partial<BoundProps> = {}): FC<BoundProps> {
  const { filter, ...rest } = extra;
  function BoundSelect(props: BoundProps): ReactElement {
    const { data } = useSWR<T[]>({ url, method: 'GET', queryParams: filter });
    return <SelectField options={data} {...props} {...rest} {...props} />;
  }

  return BoundSelect;
}
